

import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, FileText, Download, Eye, X, Loader2 
} from 'lucide-react';
import { toast } from '@/components/UI/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/UI/card";
import { Input } from "@/components/UI/input";
import { Button } from "@/components/UI/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/UI/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/UI/dialog";

import docsIcon from '@/assets/docs.png';
import pdfIcon from '@/assets/symbol.png';
import { loamsApi } from "../../services/api";

const InvoiceFileBrowser = () => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Load all files on component mount
  useEffect(() => {
    fetchFiles();
  }, []);
  
  // Sort files by invoice number (newest first)
  const sortFilesByInvoiceNumber = (filesArray) => {
    return [...filesArray].sort((a, b) => {
      // Extract numeric part from invoice_number
      const numA = a.invoice_number ? parseInt(a.invoice_number.replace(/\D/g, '')) : 0;
      const numB = b.invoice_number ? parseInt(b.invoice_number.replace(/\D/g, '')) : 0;
      
      // Sort descending (newest first)
      return numB - numA;
    });
  };
  
  // Handle clicks outside the suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Add function to determine file type and return appropriate icon
  const getFileIcon = (filename) => {
    if (!filename) return <FileText className="w-4 h-4 text-blue-500" />;
    
    const ext = filename.split('.').pop().toLowerCase();
    
    if (ext === 'pdf') {
      return <img src={pdfIcon} alt="PDF" className="w-4 h-4" />;
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
      return <img src={docsIcon} alt="Document" className="w-4 h-4" />;
    }
    
    return <FileText className="w-4 h-4 text-blue-500" />;
  };
  
  // Add function to extract project code
  const extractProjectCode = (filename) => {
    if (!filename) return 'N/A';
    const match = filename.match(/PJ\d{4}/i);
    return match ? match[0].toUpperCase() : 'N/A';
  };
  
  // Update filtered files when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // Sort files by invoice number when not searching
      setFilteredFiles(sortFilesByInvoiceNumber(files));
      setSuggestions([]);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    
    // Generate filtered files based on search term
    const fileMatches = files.filter(file => 
      file.filename.toLowerCase().includes(term) ||
      (file.invoice_number && file.invoice_number.toLowerCase().includes(term)) ||
      (file.project_name && file.project_name.toLowerCase().includes(term)) ||
      (extractProjectCode(file.filename).toLowerCase().includes(term))
    );
    
    // Sort the filtered results
    setFilteredFiles(sortFilesByInvoiceNumber(fileMatches));
    
    // Generate suggestions
    const newSuggestions = [];
    
    // Add filename suggestions
    files.forEach(file => {
      if (file.filename.toLowerCase().includes(term)) {
        const suggestion = {
          type: 'filename',
          text: file.filename,
          key: file.key
        };
        
        // Only add if not already in suggestions
        if (!newSuggestions.some(s => s.text === file.filename)) {
          newSuggestions.push(suggestion);
        }
      }
      
      // Add invoice number suggestions
      if (file.invoice_number && file.invoice_number.toLowerCase().includes(term)) {
        const suggestion = {
          type: 'invoice',
          text: `Invoice #${file.invoice_number}`,
          key: file.key
        };
        
        // Only add if not already in suggestions
        if (!newSuggestions.some(s => s.text === suggestion.text)) {
          newSuggestions.push(suggestion);
        }
      }
      
      // Add project code suggestions
      const projectCode = extractProjectCode(file.filename);
      if (projectCode.toLowerCase().includes(term)) {
        const suggestion = {
          type: 'projectCode',
          text: `Project: ${projectCode}`,
          key: file.key
        };
        
        if (!newSuggestions.some(s => s.text === suggestion.text)) {
          newSuggestions.push(suggestion);
        }
      }
    });
    
    setSuggestions(newSuggestions.slice(0, 5));
    setShowSuggestions(newSuggestions.length > 0);
    
  }, [searchTerm, files]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      
      // Call the API method to list all files
      const response = await loamsApi.invoiceFiles.listFiles();
      
      if (response && response.status === 'success') {
        // Sort files by invoice number
        const sortedFiles = sortFilesByInvoiceNumber(response.files || []);
        setFiles(sortedFiles);
        setFilteredFiles(sortedFiles);
      } else {
        toast({
          title: "Error",
          description: "Failed to load invoice files",
          variant: "destructive",
        });
        setFiles([]);
        setFilteredFiles([]);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load invoice files",
        variant: "destructive",
      });
      setFiles([]);
      setFilteredFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredFiles(sortFilesByInvoiceNumber(files));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    switch (suggestion.type) {
      case 'filename':
        setSearchTerm(suggestion.text);
        break;
      case 'invoice':
        setSearchTerm(suggestion.text.replace('Invoice #', ''));
        break;
      case 'projectCode':
        setSearchTerm(suggestion.text.replace('Project: ', ''));
        break;
      default:
        setSearchTerm(suggestion.text);
    }
    
    setShowSuggestions(false);
    
    // Find the file and scroll to it
    const file = files.find(f => f.key === suggestion.key);
    if (file) {
      // Highlight this file somehow
      setSelectedFile(file);
      setTimeout(() => {
        const element = document.getElementById(`file-row-${suggestion.key.replace(/[^\w-]/g, '_')}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  };

  const handleViewFile = async (file) => {
    try {
      setSelectedFile(file);
      setLoading(true);
      
      const url = await loamsApi.invoiceFiles.getViewUrl(file.key);
      setPreviewUrl(url);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error getting file URL:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to open file",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFile = async (file) => {
    try {
      await loamsApi.invoiceFiles.openFile(file.key);
    } catch (error) {
      console.error('Error opening file:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to open file",
        variant: "destructive",
      });
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      setLoading(true);
      const url = await loamsApi.invoiceFiles.getViewUrl(file.key);
      
      // Create an anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: "File download started",
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download file",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Invoice Files</span>
          <div className="relative w-64">
            <div className="relative">
              <Input
                ref={searchInputRef}
                placeholder="Search files..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pr-8"
                onFocus={() => setShowSuggestions(suggestions.length > 0)}
              />
              {searchTerm && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute right-8 top-0 h-full"
                  onClick={handleClearSearch}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-0 top-0 h-full"
                disabled={true}
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Suggestions dropdown */}
            {showSuggestions && (
              <div 
                ref={suggestionsRef}
                className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden"
              >
                <ul className="py-1">
                  {suggestions.map((suggestion, index) => (
                    <li 
                      key={index} 
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion.type === 'filename' && <FileText className="w-4 h-4 mr-2 text-blue-500" />}
                      {suggestion.type === 'invoice' && <FileText className="w-4 h-4 mr-2 text-green-500" />}
                      {suggestion.type === 'projectCode' && <FileText className="w-4 h-4 mr-2 text-orange-500" />}
                      <span className="truncate">{suggestion.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader className="bg-[#2980b9] text-white">
                <TableRow>
                  <TableHead className="text-white font-medium">Filename</TableHead>
                  <TableHead className="text-white font-medium">Invoice #</TableHead>
                  <TableHead className="text-white font-medium">Project Code</TableHead>
                  <TableHead className="text-white font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No invoice files found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFiles.map((file) => (
                    <TableRow 
                      key={file.key}
                      id={`file-row-${file.key.replace(/[^\w-]/g, '_')}`}
                      className={selectedFile?.key === file.key ? "bg-blue-50" : ""}
                    >
                      <TableCell className="font-medium max-w-md truncate">
                        <div className="flex items-center gap-2">
                          {getFileIcon(file.filename)}
                          <span className="truncate">{file.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell>{file.invoice_number || 'N/A'}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {extractProjectCode(file.filename)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleViewFile(file)}
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleOpenFile(file)}
                            title="Open in new tab"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDownloadFile(file)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {filteredFiles.length} of {files.length} files
            </div>
          </>
        )}
      </CardContent>

      {/* File Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedFile?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="w-full h-[75vh] border rounded">
            {previewUrl && (
              <iframe 
                src={previewUrl} 
                className="w-full h-full"
                title="Document Preview"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setPreviewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              variant="default"
              onClick={() => selectedFile && handleDownloadFile(selectedFile)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InvoiceFileBrowser;